import { useEffect, useState, useRef, useCallback } from 'react';
import { getPatientVisitsSkeleton, getPatientVisitsDetails } from '../../../../api';

export function useVisitsSkeleton({ patientId }) {
  const [skeleton, setSkeleton] = useState(null);
  const [detailsMap, setDetailsMap] = useState({});
  const [loadingProviders, setLoadingProviders] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const fetchingProviders = useRef(new Set());

  const load = useCallback(
    (page) => {
      if (!patientId) return;
      setSkeleton(null);
      setDetailsMap({});
      setLoadingProviders(new Set());
      fetchingProviders.current = new Set();
      setLoading(true);

      getPatientVisitsSkeleton(patientId, page)
        .then((res) => {
          setSkeleton(res.data);
          setLoading(false);
        })
        .catch(() => {
          setSkeleton([]);
          setLoading(false);
        });
    },
    [patientId],
  );

  // Fetch details per provider when skeleton arrives
  useEffect(() => {
    if (!skeleton?.length || !patientId) return;

    const groups = skeleton.reduce((acc, item) => {
      if (!acc[item.provider_id]) acc[item.provider_id] = [];
      acc[item.provider_id].push(item.id);
      return acc;
    }, {});

    const newProviders = Object.entries(groups).filter(
      ([pid]) => !fetchingProviders.current.has(Number(pid)),
    );

    if (!newProviders.length) return;

    const newProviderIds = newProviders.map(([id]) => Number(id));
    newProviderIds.forEach((id) => fetchingProviders.current.add(id));

    setLoadingProviders((prev) => {
      const next = new Set(prev);
      newProviderIds.forEach((id) => next.add(id));
      return next;
    });

    newProviders.forEach(([providerId, ids]) => {
      const numericId = Number(providerId);

      getPatientVisitsDetails(patientId, numericId, ids)
        .then((res) => {
          setDetailsMap((prev) => {
            const next = { ...prev };
            res.data.forEach((item) => {
              next[item.id] = item;
            });
            return next;
          });
        })
        .catch(() => {})
        .finally(() => {
          fetchingProviders.current.delete(numericId);
          setLoadingProviders((prev) => {
            const next = new Set(prev);
            next.delete(numericId);
            return next;
          });
        });
    });
  }, [skeleton]); // eslint-disable-line react-hooks/exhaustive-deps

  return { skeleton, detailsMap, loadingProviders, loading, load };
}
